<template> 
    <div tabindex="-1" ref="el-image-viewer__wrapper" class="cpt-chat-image-viewer__wrapper" :style="{ 'z-index': viewerZIndex }">
      <div class="el-image-viewer__mask" @click.stop="handleMaskClick"></div>
      <!-- CLOSE -->
      <span class="el-image-viewer__btn el-image-viewer__close" @click.stop="close">
        <i class="el-icon-close"></i>
      </span>
      <!-- ARROW -->
      <template v-if="!isSingle">
        <span
          class="el-image-viewer__btn el-image-viewer__prev"
          :class="{ 'is-disabled': !infinite && isFirst }"
          @click="prev">
          <i class="el-icon-arrow-left"/>
        </span>
        <span
          class="el-image-viewer__btn el-image-viewer__next"
          :class="{ 'is-disabled': !infinite && isLast }"
          @click="next">
          <i class="el-icon-arrow-right"/>
        </span>
      </template>
      <!-- ACTIONS -->
      <div class="el-image-viewer__btn el-image-viewer__actions">
        <div class="el-image-viewer__actions__inner">
          <i class="el-icon-zoom-out pointer" @click="handleActions('zoomOut')"></i>
          <i class="el-icon-zoom-in pointer" @click="handleActions('zoomIn')"></i>
          <i class="el-image-viewer__actions__divider"></i>
          <i :class="[mode.icon, 'pointer']" @click="toggleMode"></i>
          <i class="el-image-viewer__actions__divider"></i>
          <i v-if="!isLongImg" class="el-icon-refresh-left pointer" @click="handleActions('anticlocelise')"></i>
          <i v-if="!isLongImg" class="el-icon-refresh-right pointer" @click="handleActions('clocelise')"></i>
          <i class="el-icon-download pointer" @click="handleActions('download')"></i>
          <i class="el-icon-document-copy pointer" @click="handleActions('copy')"></i>
        </div>
      </div>
      <!-- CANVAS -->
      <div class="el-image-viewer__canvas" ref="canvas">
        <template v-if="isLongImg">
          <div 
          class="scroll-container" 
          ref="scroll" 
          :style="{
            width: image_width + 'px',
          }">
            <img
              ref="img"
              class="el-image-viewer__img disabled-prevent-contextmenu long-image"
              :src="currentImg"
              :style="imgStyle"
              referrerpolicy='no-referrer'
              @load="handleImgLoad"
              @error="handleImgError"
              @mousedown="handleMouseDown"
              @contextmenu="handleContextMenu"
            >
          </div>
        </template>
        <template v-else>
          <img
            ref="img"
            class="el-image-viewer__img disabled-prevent-contextmenu"
            :src="currentImg"
            :style="imgStyle"
            referrerpolicy='no-referrer'
            @load="handleImgLoad"
            @error="handleImgError"
            @mousedown="handleMouseDown"
            @contextmenu="handleContextMenu"
          >
        </template>
      </div>

      <div 
        v-if="isLongImg" 
        class="el-image-viewer__overview" 
        ref="overview"
        :style="{
          right: overview_right + 'px',
          transition: transform.enableTransition ? 'all 0.15s ease 0s' : '',
          opacity: initOffsetY ? '1' : '0'
        }"
      >
        <div class="overview-container">
          <img
            ref="overview-img"
            class="el-image-viewer__img disabled-prevent-contextmenu"
            :src="currentImg"
            referrerpolicy='no-referrer'
          >
          <div class="mask"></div>
          <div class="viewport-slider">
            <div 
              class="slider"
              :style="sliderStyle"
              @mousedown="handleSliderMouseDown">
              <img
                class="el-image-viewer__img disabled-prevent-contextmenu slider-img"
                :src="currentImg"
                :style="{
                  top: `-${slider_transform.offsetY + 2}px`,
                  left: `-${slider_transform.offsetX + 2}px`
                }"
                referrerpolicy='no-referrer'
              >
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import Vue from "vue"
import { copyText, copyImage, throttle, sleep } from "_lib/utils/utils"

import { downloadImage } from "_lib/utils/utilsFile";
import { PopupManager } from 'element-ui/lib/utils/popup';


const on = (function() {
  if (document.addEventListener) {
    return function(element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function(element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
})();

/* istanbul ignore next */
const off = (function() {
  if (document.removeEventListener) {
    return function(element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function(element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
})();

const isFirefox = function() {
  return !Vue.prototype.$isServer && !!window.navigator.userAgent.match(/firefox/i);
};


const Mode = {
  CONTAIN: {
    name: 'contain',
    icon: 'el-icon-full-screen'
  },
  ORIGINAL: {
    name: 'original',
    icon: 'el-icon-c-scale-to-original'
  }
};

const ZOOM_RATE = 0.2;
const HEADER_HEIGHT = 30;

const mousewheelEventName = isFirefox() ? 'DOMMouseScroll' : 'mousewheel';

function rafThrottle(fn) {
  let locked = false;
  return function(...args) {
    if (locked) return;
    locked = true;
    window.requestAnimationFrame(_ => {
      fn.apply(this, args);
      locked = false;
    });
  };
}


export default {
  name: 'ImagePreviewer',
  props: {
    urlList: {
      type: Array,
      default: () => []
    },
    zIndex: {
      type: Number,
      default: 2000
    },
    onSwitch: {
      type: Function,
      default: () => {}
    },
    onClose: {
      type: Function,
      default: () => {}
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      index: this.initialIndex,
      infinite: true,
      loading: false,
      isLongImg: true,
      mode: Mode.CONTAIN,
      initOffsetX: 0,
      initOffsetY: 0,
      image_width: 600,
      image_height: 0,
      overview_right: 18,
      transform: {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false,
        origin: {
          x: 'center',
          y: 'center',
        },
      },
      slider_transform: {
        offsetX: 0,
        offsetY: 0,
        height: 50,
        width: 24,
      },
      lastXY: {
        x: 0,
        y: 0,
      },
      currentXY: {
        x: 0,
        y: 0,
      },
      scaleStart: {
        x: 0,
        y: 0,
        distance: 0
      }
    };
  },
  computed: {
    realUrlList () {
      return this.urlList.map(item => item.split('?')[0])
    },
    isSingle() {
      return this.realUrlList.length <= 1;
    },
    isFirst() {
      return this.index === 0;
    },
    isLast() {
      return this.index === this.realUrlList.length - 1;
    },
    currentImg() {
      return this.realUrlList[this.index];
    },
    imgStyle() {
      const { scale, deg, offsetX, offsetY, enableTransition } = this.transform;
      
      const style = {
        transform: `scale(${scale}) rotate(${deg}deg) translateZ(0)`,
        display: this.loading ? 'none' : 'block',
        transformOrigin: `${this.transform.origin.x} ${this.transform.origin.y}`,
        transition: enableTransition ? 'all 0.15s ease 0s' : '',
        'left': `${this.initOffsetX + offsetX}px`,
        'top': `${this.initOffsetY + offsetY}px`,
      };
      if (!this.isLongImg && this.mode === Mode.CONTAIN) {
        style.maxWidth = style.maxHeight = '100%';
      }
      return style;
    },
    sliderStyle() {
      const { offsetX, offsetY, height, width } = this.slider_transform;
      const style = {
        left: `${offsetX}px`,
        top: `${offsetY}px`,
        height: `${height}px`,
        width: `${width}px`,
      };
      return style;
    },
    viewerZIndex() {
      const nextZIndex = PopupManager.nextZIndex();
      return this.zIndex > nextZIndex ? this.zIndex : nextZIndex;
    }
  },
  watch: {
    index: {
      handler: function(val) {
        this.reset();
        this.onSwitch(val);
      }
    },
    currentImg(val) {
      this.judgeImgType(val)
      this.$nextTick(_ => {
        const $img = this.$refs.img;
        if (!$img.complete) {
          this.loading = true;
        }
      });
    },
    isLongImg: {
      handler: async function (val) {
        this.reset()
        let canvas = this.$refs.canvas
        this.$nextTick(() => {
          if(val) {
            while(!this.$refs.scroll) sleep(100)
            on(this.$refs.scroll, 'scroll', this.handleImgScroll)
            on(window, 'resize', this.handleWindowResize)
            off(canvas, mousewheelEventName, this._mouseWheelHandler);
          }
          else {
            on(canvas, mousewheelEventName, this._mouseWheelHandler);
            off(this.$refs.scroll, 'scroll', this.handleImgScroll)
            off(window, 'resize', this.handleWindowResize)
          }
        })
      }
    },
    'transform.scale': {
      handler: function(val) {
        if(!this.isLongImg) return
        const containerHeight = document.documentElement.clientHeight
        const containerWidth = document.documentElement.clientWidth
        const newHeight = this.$refs.img.height * val
        const newWidth = this.$refs.img.width * val
        if(this.$refs.img) {
          this.initOffsetY = -(containerHeight - newHeight) / 2
          this.initOffsetX = (newWidth - containerWidth) / 2
          if(this.initOffsetX < 0) this.initOffsetX = 0
          this.image_width = newWidth
          this.image_height = newHeight
          this.overview_right = (containerWidth - this.image_width) / 2 - 30 // 减去自身宽度及与图片的距离
          if(this.overview_right < 0) this.overview_right = 18 // 防止概览图溢出屏幕
          if(this.$refs.scroll) this.$refs.scroll.height = this.image_height
        }
        if(this.$refs['overview-img']) {
          this.slider_transform.height = containerHeight / newHeight * this.$refs['overview-img'].height
          if(newWidth > containerWidth) this.slider_transform.width = containerWidth / newWidth * this.$refs['overview-img'].width
          else this.slider_transform.width = this.$refs['overview-img'].width
        }
        if(this.$refs.scroll && this.$refs.scroll.scrollTop > this.image_height - containerHeight + HEADER_HEIGHT) {
          this.$refs.scroll.scrollTop = this.image_height - containerHeight + HEADER_HEIGHT
        }
      },
      deep: true
    },
  },
  methods: {
    close() {
      this.deviceSupportUninstall();
      this.$emit('close')
    },
    deviceSupportInstall() {
      this._keyDownHandler = e => {
        e.stopPropagation();
        const keyCode = e.keyCode;
        switch (keyCode) {
          // ESC
          case 27:
            this.close();
            break;
          // SPACE
          case 32:
            this.toggleMode();
            break;
          // LEFT_ARROW
          case 37:
            this.prev();
            break;
          // UP_ARROW
          case 38:
            this.handleActions('zoomIn');
            break;
          // RIGHT_ARROW
          case 39:
            this.next();
            break;
          // DOWN_ARROW
          case 40:
            this.handleActions('zoomOut');
            break;
        }
      };
      this._mouseWheelHandler = rafThrottle(e => {
        if (!e.deltaY) {
          return;
        }
        let origin = {
          x: e.offsetX + 'px',
          y: e.offsetY + 'px',
        }

        let zoomRate = ZOOM_RATE
        if (e.deltaY >= 0 && this.transform.scale - zoomRate < 0.1) {
          return
        }
        
        this.currentXY = {
          x: e.offsetX,
          y: e.offsetY,
        }
        
        if (e.deltaY < 0) {
          this.handleActions('zoomIn', {
            enableTransition: true,
            origin,
          });
        } else {
          this.handleActions('zoomOut', {
            enableTransition: true,
            origin,
          });
        }
      });
      let canvas = this.$refs.canvas
      on(canvas, 'keydown', this._keyDownHandler);
      if(this.isLongImg) {
        on(this.$refs.scroll, 'scroll', this.handleImgScroll)
        on(window, 'resize', this.handleWindowResize)
      }
      else on(canvas, mousewheelEventName, this._mouseWheelHandler);
    },
    deviceSupportUninstall() {
      let canvas = this.$refs.canvas
      off(canvas, 'keydown', this._keyDownHandler);
      off(this.$refs.scroll, 'scroll', this.handleImgScroll)
      off(window, 'resize', this.handleWindowResize)
      off(canvas, mousewheelEventName, this._mouseWheelHandler);
      off(document, 'mousemove', this._dragHandler);
      off(document, 'mousemove', this._dragSliderHandler);
      off(document, 'mouseup', this._dragEndHandler);
      off(document, 'mouseup', this._dragEndSliderHandler);
      
      this._keyDownHandler = null;
      this._mouseWheelHandler = null;
      this._dragHandler = null;
      this._dragEndHandler = null;
      this._dragSliderHandler = null;
      this._dragEndSliderHandler = null;
    },
    handleWindowResize: throttle(function() {
      const containerHeight = document.documentElement.clientHeight
      const containerWidth = document.documentElement.clientWidth
      this.initOffsetY = (this.image_height - containerHeight) / 2
      this.initOffsetX = (this.image_width - containerWidth) / 2
      if(this.initOffsetX < 0) this.initOffsetX = 0
      if(this.img_width > containerWidth) this.slider_transform.width = containerWidth / this.img_width * this.$refs['overview-img'].width
      else this.slider_transform.width = this.$refs['overview-img'].width
      this.slider_transform.height = containerHeight / this.image_height * this.$refs['overview-img'].height
      this.overview_right = (containerWidth - this.image_width) / 2 - 30 // 减去自身宽度及与图片的距离
      if(this.overview_right < 0) this.overview_right = 18 // 防止概览图溢出屏幕
    }, 100),
    handleImgScroll() {
      const containerHeight = document.documentElement.clientHeight
      const containerWidth = document.documentElement.clientWidth
      if(this.$refs.scroll.scrollTop > this.image_height - containerHeight + HEADER_HEIGHT) {
        this.$refs.scroll.scrollTop = this.image_height - containerHeight + HEADER_HEIGHT
      }
      if(this.$refs.scroll.scrollLeft > this.image_width - containerWidth) {
        this.$refs.scroll.scrollLeft = this.image_width - containerWidth
      }
      this.slider_transform.offsetY = this.$refs.scroll.scrollTop / this.image_height * this.$refs['overview-img'].height
      this.slider_transform.offsetX = this.$refs.scroll.scrollLeft / this.image_width * this.$refs['overview-img'].width
    },
    handleImgLoad(e) { 
      if(this.isLongImg) {
        this.transform.scale = parseFloat((600 / this.$refs.img.width).toFixed(3))
        let containerHeight = document.documentElement.clientHeight - HEADER_HEIGHT;
        let newHeight = this.$refs.img.height * this.transform.scale
        this.slider_transform.height = containerHeight / newHeight * this.$refs['overview-img'].height
        this.initOffsetY = -(containerHeight - newHeight) / 2;
      }
      this.loading = false;
    },
    handleImgError(e) {
      this.loading = false;
      e.target.alt = '加载失败';
    },
    handleMouseDown(e) {
      if (this.loading || e.button !== 0) return;
      const { offsetX, offsetY } = this.transform;
      let startX = e.pageX;
      let startY = e.pageY;
      this._dragHandler = ev => {
        if(!this.isLongImg) {
          this.transform.offsetX = offsetX + ev.pageX - startX;
          this.transform.offsetY = offsetY + ev.pageY - startY;
        }
        else {
          this.$refs.scroll.scrollTop = this.$refs.scroll.scrollTop - ev.pageY + startY;
          this.$refs.scroll.scrollLeft = this.$refs.scroll.scrollLeft - ev.pageX + startX;
          startY = ev.pageY
        }
        this.transform.enableTransition = false
      };
      this._dragEndHandler = ev => {
        off(document, 'mousemove', this._dragHandler);
      };

      on(document, 'mousemove', this._dragHandler);
      on(document, 'mouseup', this._dragEndHandler);
      this.showContextMenu && this.$contextmenu.destroy()
      e.preventDefault();
    },
    handleSliderMouseDown(e) {
      if (this.loading || e.button !== 0) return;
      const { offsetX, offsetY, height, width } = this.slider_transform;
      const startX = e.pageX;
      const startY = e.pageY;
      const overview_height = this.$refs['overview-img'].height
      const overview_width = this.$refs['overview-img'].width
      const img_height = this.$refs['img'].height * this.transform.scale
      const img_width = this.$refs['img'].width * this.transform.scale
      this._dragSliderHandler = ev => {
        this.slider_transform.offsetX = offsetX + ev.pageX - startX;
        this.slider_transform.offsetY = offsetY + ev.pageY - startY;
        if(this.slider_transform.offsetY < 0) {
          this.slider_transform.offsetY = 0
        }
        if(this.slider_transform.offsetY > overview_height - height) {
          this.slider_transform.offsetY = overview_height - height
        }
        if(this.slider_transform.offsetX < 0) {
          this.slider_transform.offsetX = 0
        }
        if(this.slider_transform.offsetX > overview_width - width) {
          this.slider_transform.offsetX = overview_width - width
        }
        this.$refs.scroll.scrollTop = this.slider_transform.offsetY / overview_height * img_height
        this.$refs.scroll.scrollLeft = this.slider_transform.offsetX / overview_width * img_width
      };
      this._dragEndSliderHandler = ev => {
        off(document, 'mousemove', this._dragSliderHandler);
      };
      on(document, 'mousemove', this._dragSliderHandler);
      on(document, 'mouseup', this._dragEndSliderHandler);
      this.showContextMenu && this.$contextmenu.destroy()
      e.preventDefault();
    },
    handleMaskClick() {
      if (this.maskClosable) {
        this.close();
      }
    },
    reset() {
      this.initOffsetY = 0,
      this.image_width = 600
      this.transform = {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false,
        origin: {
          x: 'center',
          y: 'center',
        }
      };
    },
    toggleMode() {
      if (this.loading) return;

      const modeNames = Object.keys(Mode);
      const modeValues = Object.values(Mode);
      const index = modeValues.indexOf(this.mode);
      const nextIndex = (index + 1) % modeNames.length;
      this.mode = Mode[modeNames[nextIndex]];
      this.reset();
    },
    prev() {
      if (this.isFirst && !this.infinite) return;
      const len = this.realUrlList.length;
      this.index = (this.index - 1 + len) % len;
    },
    next() {
      if (this.isLast && !this.infinite) return;
      const len = this.realUrlList.length;
      this.index = (this.index + 1) % len;
    },
    handleActions(action, options = {}) {
      if (this.loading) return;
      const { rotateDeg, enableTransition, origin, zoomRate } = {
        zoomRate: ZOOM_RATE,
        rotateDeg: 90,
        enableTransition: true,
        origin: {
          x: 'center',
          y: 'center',
        },
        ...options
      };
      
      const correctOriginAndOffset = () => {
        let {scale, offsetX, offsetY} = this.transform
        let {x: lastX, y: lastY} = this.lastXY
        const offsetLeft = (scale - 1) * (this.currentXY.x - lastX) + offsetX
        const offsetTop = (scale - 1) * (this.currentXY.y - lastY) + offsetY
        this.lastXY = {
          x: this.currentXY.x,
          y: this.currentXY.y,
        }
        this.transform.offsetX = offsetLeft
        this.transform.offsetY = offsetTop
      }
      
      const { transform } = this;
      switch (action) {
        case 'zoomOut':
          correctOriginAndOffset()
          if(transform.scale - zoomRate <= 0) break
          
          transform.scale = parseFloat((transform.scale - zoomRate).toFixed(3));
          transform.origin.x = origin.x
          transform.origin.y = origin.y
          break;
        case 'zoomIn':
          correctOriginAndOffset()
          // const containerWidth = document.documentElement.clientWidth
          // if(this.$refs.img.width * (transform.scale + zoomRate) > containerWidth) return
          
          transform.scale = parseFloat((transform.scale + zoomRate).toFixed(3));
          transform.origin.x = origin.x
          transform.origin.y = origin.y
          break;
        case 'clocelise':
          transform.deg += rotateDeg;
          transform.origin.x = 'center'
          transform.origin.y = 'center'
          break;
        case 'anticlocelise':
          transform.deg -= rotateDeg;
          transform.origin.x = 'center'
          transform.origin.y = 'center'
          break;
        case 'download':
          const imageNameFromUrl = (url) => {
            let u = url.split('?')[0] // strip query
            for (let i = u.length - 1; i >= 0; i--) {
              if (u[i] === '/') {
                return u.slice(i + 1)
              }
            }
            return ''
          }

          this.$toast.success('图片下载中')
          const imageName = imageNameFromUrl(this.currentImg)
          downloadImage(this.currentImg, imageName)
          break
        case 'copy':
          try {
            copyText(this.currentImg)
            this.$toast.success('复制图片链接成功')
          } catch (error) {
            this.$toast.error('复制图片链接成功')
          }
          break;
      }
      transform.enableTransition = enableTransition;
    },
    escEvent(e) {
      if (e.keyCode === 27) {
        // 这里的settimeout是为了不和私聊面板的esc事件冲突
        setTimeout(() => {
          this.close();
        }, 0)
      }
    },
    handleContextMenu(event) {
      if (process.env.IS_CLIENT) {
        event.preventDefault();
        let optionList = [
          {
            component: 'menu-button',
            data: {
              label: '图片存储为',
            },
            onClick: () => {
              this.showContextMenu = false
              this.handleActions('download')
            },
          },
          {
            component: 'menu-button',
            data: {
              label: '复制图片',
            },
            onClick: async () => {
              this.showContextMenu = false
              this.$contextmenu.destroy()
              copyImage(this.currentImg).then(() => {
                this.$toast.success('复制成功')
              }).catch(() => {
                this.$toast.error('复制失败')
              })
            },
          },
          {
            component: 'menu-button',
            data: {
              label: '复制图片地址',
            },
            onClick: () => {
              this.showContextMenu = false
              this.handleActions('copy')
            },
          }
        ]
        this.$contextmenu({
          groups: [
            {
              items: optionList
            },
          ],
          event,
        })
        this.showContextMenu = true
      }
    },
    judgeImgType(url) {
      let img = new Image();
      img.src = url;
      img.onload = () => {
        this.isLongImg = img.height / img.width >= 3
      };
    },
  },
  mounted() {
    this.deviceSupportInstall();
    document.body.appendChild(this.$el)
    on(document, 'keydown', this.escEvent);
    this.$refs['el-image-viewer__wrapper'].focus();
    this.judgeImgType(this.currentImg);
  },
  beforeDestroy () {
    this.deviceSupportUninstall()
    off(document, 'keydown', this.escEvent);
    this.showContextMenu && this.$contextmenu.destroy()
  }
};
</script>

<style lang="scss">
.cpt-chat-image-viewer__wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  .el-image-viewer__btn {
    position: absolute;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    opacity: .8;
    cursor: pointer;
    box-sizing: border-box;
    user-select: none;
  }

  .el-image-viewer__close {
    top: 40px;
    right: 40px;
    width: 40px;
    height: 40px;
    font-size: 24px;
    color: #fff;
    background-color: #606266;
  }
  .el-image-viewer__overview {
    @include size(24px, auto);
    position: fixed;
    right: 18px;
    bottom: 18px;
    
    .el-image-viewer__img {
      width: 24px;
      border-radius: 3px;
      border: 1px #000;
      &.slider-img {
        position: relative;
        top: -2px;
        left: -2px;
      }
    }
    .mask {
      @include size();
      @include absolute(0, 0, unset, unset);
      background: $d-op-s4;
      border-radius: 3px;
    }
    .viewport-slider {
      @include size();
      @include absolute(0, 0, unset, unset);
      .slider {
        @include absolute(0, unset, unset, 0);
        @include size(100%, 50px);
        border-radius: 3px;
        border: 2px solid #7dd95e;
        overflow: hidden;
      }
      
    }
  }
  .el-image-viewer__canvas {
    width: auto;
    height: 100%;
    margin-top: 30px;
    overflow: hidden;
    .scroll-container {
      position: relative;
      width: auto;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: auto;
      // padding-top: 30px;
    }
    .el-image-viewer__img {
      position: relative;
      pointer-events: auto;
    }
    .long-img {
      object-fit: none;
    }
  }
  .el-image-viewer__actions {
    left: 50%;
    bottom: 30px;
    transform: translateX(-50%);
    width: 282px;
    height: 44px;
    padding: 0 23px;
    background-color: #606266;
    border-color: #fff;
    border-radius: 22px;
    .el-image-viewer__actions__inner {
      width: 100%;
      height: 100%;
      text-align: justify;
      cursor: default;
      font-size: 23px;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: space-around;
    }
  }
  .el-image-viewer__prev {
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    font-size: 24px;
    color: #fff;
    background-color: #606266;
    border-color: #fff;
    left: 40px;
  }
  .el-image-viewer__next {
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    font-size: 24px;
    color: #fff;
    background-color: #606266;
    border-color: #fff;
    right: 40px;
    text-indent: 2px;
  }
  .el-image-viewer__mask {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    color: $ct-bl-mk;
  }

  &:focus-visible {
    outline: unset;
  }
}
</style>
